import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Search, PlayCircle, X, Video, Youtube, ListPlus } from "lucide-react";
import { VideoPlayer, VideoUploader } from "@/components/VideoPlayer";

const EMPTY = { name: "", category: "strength", muscle_groups: "", equipment: "", instructions: "", video_url: "", image_url: "" };

export default function CoachExercises() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [savingVideo, setSavingVideo] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [importing, setImporting] = useState(false);

  const importChannel = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/exercises/import-channel");
      toast.success(`Channel import: ${data.created} added, ${data.updated} videos filled, ${data.skipped} already set`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const submitBulk = async () => {
    const items = bulkText.split("\n").map((line) => {
      const [name, video_url] = line.split("|").map((s) => (s || "").trim());
      return { name, video_url };
    }).filter((i) => i.name && i.video_url);
    if (items.length === 0) { toast.error("Add lines like: Exercise name | video URL"); return; }
    try {
      const { data } = await api.post("/exercises/bulk-videos", { items });
      toast.success(`${data.matched.length} updated, ${data.created.length} created`);
      setBulkText(""); setBulkOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openExercise = (ex) => {
    setSelected(ex);
    setEditUrl(ex.video_url || "");
  };

  const saveVideo = async () => {
    setSavingVideo(true);
    try {
      const { data } = await api.patch(`/exercises/${selected.exercise_id}`, { video_url: editUrl });
      setSelected(data);
      toast.success("Demo video updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingVideo(false); }
  };

  const load = () => api.get("/exercises", { params: q ? { q } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/exercises", {
        ...form,
        muscle_groups: form.muscle_groups.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Exercise added");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete exercise?")) return;
    await api.delete(`/exercises/${id}`);
    setSelected(null);
    load();
  };

  return (
    <div className="space-y-10" data-testid="coach-exercises">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Library</div>
          <h1 className="font-serif text-5xl mt-3">Exercises</h1>
          <p className="text-secondary-luxe text-sm mt-3">Add demo videos via YouTube/Vimeo links or upload your own recordings.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-secondary" onClick={importChannel} disabled={importing} data-testid="import-channel-btn">
            <Youtube size={14} /> {importing ? "Importing…" : "Import my channel demos"}
          </button>
          <button className="btn-secondary" onClick={() => setBulkOpen(!bulkOpen)} data-testid="bulk-videos-btn">
            <ListPlus size={14} /> Bulk add videos
          </button>
          <button className="btn-primary" onClick={() => setOpen(!open)} data-testid="new-exercise-btn">
            <Plus size={14} /> New exercise
          </button>
        </div>
      </header>

      {bulkOpen && (
        <div className="card-luxe space-y-4 max-w-3xl" data-testid="bulk-videos-form">
          <div className="overline">Bulk add videos</div>
          <p className="text-sm text-secondary-luxe">One per line: <span className="font-mono-luxe">Exercise name | video URL</span>. Matching exercises get the video; new names are created.</p>
          <textarea className="input-luxe font-mono-luxe text-xs" rows={7} value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={"Barbell Hip Thrust | https://www.youtube.com/watch?v=NBgbpQpVsD0\nKettlebell Swing | https://www.youtube.com/watch?v=Pjh0zeRjaMM"}
            data-testid="bulk-videos-textarea" />
          <button className="btn-primary" onClick={submitBulk} data-testid="bulk-videos-submit">Apply videos</button>
        </div>
      )}

      <div className="flex items-center gap-3 max-w-md">
        <Search size={16} className="text-secondary-luxe" />
        <input className="input-luxe" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="exercise-search" />
      </div>

      {open && (
        <form onSubmit={submit} className="card-luxe space-y-5 max-w-3xl" data-testid="exercise-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-luxe">Name</label>
              <input className="input-luxe" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="exercise-name" />
            </div>
            <div>
              <label className="label-luxe">Category</label>
              <select className="input-luxe" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="strength">Strength</option>
                <option value="cardio">Cardio</option>
                <option value="mobility">Mobility</option>
                <option value="core">Core</option>
              </select>
            </div>
            <div>
              <label className="label-luxe">Muscle groups (comma sep)</label>
              <input className="input-luxe" value={form.muscle_groups} onChange={(e) => setForm({ ...form, muscle_groups: e.target.value })} />
            </div>
            <div>
              <label className="label-luxe">Equipment</label>
              <input className="input-luxe" value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="label-luxe">Instructions</label>
              <textarea className="input-luxe" rows={3} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
            </div>
            <div className="md:col-span-2 space-y-3">
              <label className="label-luxe">Demo video</label>
              <input className="input-luxe" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                placeholder="Paste YouTube / Vimeo link…" data-testid="exercise-video-url" />
              <div className="flex items-center gap-3">
                <span className="font-label text-[10px] tracking-[0.26em] uppercase text-tertiary-luxe">or</span>
                <VideoUploader onUploaded={(url) => setForm((f) => ({ ...f, video_url: url }))} />
              </div>
              {form.video_url && <VideoPlayer url={form.video_url} />}
            </div>
          </div>
          <button className="btn-primary" data-testid="exercise-submit">Add exercise</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((ex) => (
          <button key={ex.exercise_id} className="card-luxe text-left hover:border-luxe-strong" onClick={() => openExercise(ex)} data-testid={`exercise-${ex.exercise_id}`}>
            <div className="flex items-center justify-between">
              <div className="overline">{ex.category}</div>
              {ex.video_url && <Video size={14} style={{ color: "var(--accent)" }} data-testid={`has-video-${ex.exercise_id}`} />}
            </div>
            <h3 className="font-serif text-xl mt-2">{ex.name}</h3>
            <div className="text-xs text-secondary-luxe mt-2">{(ex.muscle_groups || []).join(" · ")}</div>
            {ex.instructions && <p className="text-sm text-secondary-luxe mt-3 line-clamp-3">{ex.instructions}</p>}
            <div className="mt-4 flex items-center justify-between text-xs text-tertiary-luxe">
              <span>{ex.equipment}</span>
              {ex.video_url && <span className="flex items-center gap-1" style={{ color: "var(--accent)" }}><PlayCircle size={12} /> Watch demo</span>}
            </div>
          </button>
        ))}
        {items.length === 0 && <div className="text-secondary-luxe">No exercises found.</div>}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(10,9,8,0.72)" }} onClick={() => setSelected(null)} data-testid="exercise-modal">
          <div className="card-luxe max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">{selected.category}</div>
                <h2 className="font-serif text-3xl mt-2">{selected.name}</h2>
              </div>
              <button className="btn-ghost" onClick={() => setSelected(null)} data-testid="close-exercise-modal"><X size={16} /></button>
            </div>
            {selected.video_url ? (
              <div className="mt-5"><VideoPlayer url={selected.video_url} /></div>
            ) : (
              <p className="text-sm text-tertiary-luxe mt-5">No demo video yet — add one below.</p>
            )}
            <div className="mt-6 pt-5 space-y-3" style={{ borderTop: "1px solid var(--border)" }} data-testid="edit-video-block">
              <label className="label-luxe">{selected.video_url ? "Change demo video" : "Add demo video"}</label>
              <input className="input-luxe" value={editUrl} onChange={(e) => setEditUrl(e.target.value)}
                placeholder="Paste YouTube / Vimeo link…" data-testid="edit-video-url" />
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-label text-[10px] tracking-[0.26em] uppercase text-tertiary-luxe">or</span>
                <VideoUploader onUploaded={(url) => setEditUrl(url)} />
                <button className="btn-primary text-xs ml-auto" onClick={saveVideo}
                  disabled={savingVideo || editUrl === (selected.video_url || "")} data-testid="save-video-btn">
                  {savingVideo ? "Saving…" : "Save video"}
                </button>
              </div>
              {editUrl && editUrl !== selected.video_url && <VideoPlayer url={editUrl} />}
            </div>
            <div className="text-xs text-secondary-luxe mt-4">{(selected.muscle_groups || []).join(" · ")} {selected.equipment ? `· ${selected.equipment}` : ""}</div>
            {selected.instructions && <p className="text-sm text-secondary-luxe mt-4 whitespace-pre-wrap">{selected.instructions}</p>}
            <div className="mt-6 flex justify-end">
              <button className="btn-ghost" onClick={() => remove(selected.exercise_id)} data-testid={`delete-ex-${selected.exercise_id}`}><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
